import { For, Show, createEffect } from "solid-js";
import type { DuelStatus } from "system";
import { DuelCard } from "~/components/duel-card";
import { PageContainer, PageHeader } from "~/components/layout/page";
import { PendingChangesButton } from "~/components/pending-changes-button";
import { RulesDialog } from "~/components/rules-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { duelsApi } from "~/lib/api";
import { useAuth } from "~/lib/auth";
import { requireFeature } from "~/lib/features";
import { QueryKey } from "~/lib/query-keys";

import { createQuery } from "@tanstack/solid-query";
import { Link, createFileRoute } from "@tanstack/solid-router";

function validateEnum<T extends string>(
  value: unknown,
  options: readonly T[],
): T | undefined {
  return (options as unknown as string[]).includes(value as string)
    ? (value as T)
    : undefined;
}

const DUEL_TAB_VALUES = ["live", "completed", "disputed"] as const;
type DuelTab = (typeof DUEL_TAB_VALUES)[number];

export const Route = createFileRoute("/_authed/duels/")({
  beforeLoad: () => requireFeature("duels"),
  validateSearch: (search: Record<string, unknown>) => ({
    tab: validateEnum(search.tab, DUEL_TAB_VALUES),
  }),
  component: DuelsPage,
});

const TABS: { value: string; label: string; statuses: DuelStatus[] }[] = [
  { value: "live", label: "Live", statuses: ["pending", "active"] },
  { value: "completed", label: "Completed", statuses: ["completed"] },
  { value: "disputed", label: "Disputed", statuses: ["disputed"] },
];

function DuelsPage() {
  const { state } = useAuth();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab = () => search().tab ?? "live";
  const setTab = (t: string) =>
    navigate({
      search: (prev) => ({ ...prev, tab: t as DuelTab }),
      replace: true,
    });

  // Refetch aggressively only while the current user has live duels.
  const duels = createQuery(() => ({
    queryKey: [QueryKey.Duels, "all"],
    queryFn: () => duelsApi.list(),
  }));

  // Judges/admins with no live duel of their own land on Disputed by default,
  // but only when no tab is already specified in the URL.
  let autoSwitched = false;
  createEffect(() => {
    if (autoSwitched || !duels.data || search().tab !== undefined) return;
    autoSwitched = true;
    const isJudge =
      state.user?.role === "admin" || state.user?.role === "judge";
    if (!isJudge) return;
    const hasMyLive = duels.data.some(
      (d) =>
        (d.status === "pending" || d.status === "active") &&
        (d.attackerId === state.user?.id || d.defenderId === state.user?.id),
    );
    const hasDisputed = duels.data.some(
      (d) =>
        (d.status === "disputed") &&
        (d.attackerId !== state.user?.id && d.defenderId !== state.user?.id),
    );
    if (!hasMyLive && hasDisputed) setTab("disputed");
  });

  const isMyDuel = (d: { attackerId: string; defenderId: string }) =>
    d.attackerId === state.user?.id || d.defenderId === state.user?.id;

  const tabCount = (statuses: DuelStatus[]) =>
    (duels.data ?? []).filter((d) => statuses.includes(d.status)).length;

  const filtered = () => {
    const current = TABS.find((t) => t.value === tab());
    if (!current) return duels.data ?? [];
    return (duels.data ?? []).filter((d) =>
      current.statuses.includes(d.status),
    );
  };

  const myLive = () => filtered().filter(isMyDuel);
  const otherLive = () => filtered().filter((d) => !isMyDuel(d));

  return (
    <PageContainer>
      <div class="flex items-start justify-between gap-4">
        <PageHeader
          title="Duel Center"
          description="All active and past duels across the group"
        ></PageHeader>
        <div class="flex items-center gap-2">
          <RulesDialog scope="duels" />
          <PendingChangesButton class="shrink-0" />
        </div>
      </div>

      <Tabs value={tab()} onChange={setTab}>
        <TabsList>
          <For each={TABS}>
            {(t) => (
              <TabsTrigger value={t.value}>
                {t.label}
                <Show when={tabCount(t.statuses) > 0}>
                  <span class="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-brand/20 text-brand text-label-xs font-bold">
                    {tabCount(t.statuses)}
                  </span>
                </Show>
              </TabsTrigger>
            )}
          </For>
        </TabsList>

        {/* Live tab — own duels pinned above the rest */}
        <TabsContent value="live">
          <Show when={duels.data} fallback={<GridSkeleton />}>
            <Show
              when={filtered().length > 0}
              fallback={
                <p class="text-center text-text-faint py-16 text-sm leading-relaxed">
                  No live duels right now. Find a peer at your tier or anyone above you in{" "}
                  <Link
                    to="/rankings"
                    search={{ rt: undefined, cc: undefined, q: undefined }}
                    class="text-brand hover:underline font-semibold"
                  >
                    Rankings
                  </Link>{" "}
                  and challenge them.
                </p>
              }
            >
              <div class="space-y-6">
                <Show when={myLive().length > 0}>
                  <div class="space-y-3">
                    <p class="text-xs font-semibold uppercase tracking-widest text-text-faint">
                      Your Duel
                    </p>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <For each={myLive()}>
                        {(duel) => <DuelCard duel={duel} />}
                      </For>
                    </div>
                  </div>
                </Show>
                <Show when={otherLive().length > 0}>
                  <div class="space-y-3">
                    <Show when={myLive().length > 0}>
                      <p class="text-xs font-semibold uppercase tracking-widest text-text-faint">
                        Community
                      </p>
                    </Show>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <For each={otherLive()}>
                        {(duel) => <DuelCard duel={duel} />}
                      </For>
                    </div>
                  </div>
                </Show>
              </div>
            </Show>
          </Show>
        </TabsContent>

        {/* Completed + Disputed tabs — flat grid */}
        <For each={TABS.filter((t) => t.value !== "live")}>
          {(t) => (
            <TabsContent value={t.value}>
              <Show when={duels.data} fallback={<GridSkeleton />}>
                <Show
                  when={filtered().length > 0}
                  fallback={
                    <p class="text-center text-text-faint py-16 text-sm">
                      No duels here.
                    </p>
                  }
                >
                  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <For each={filtered()}>
                      {(duel) => <DuelCard duel={duel} />}
                    </For>
                  </div>
                </Show>
              </Show>
            </TabsContent>
          )}
        </For>
      </Tabs>
    </PageContainer>
  );
}

function GridSkeleton() {
  return (
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <For each={Array(6)}>
        {() => <div class="h-40 rounded-xl bg-surface-2 animate-pulse" />}
      </For>
    </div>
  );
}
